import { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Card, Badge, Button, Modal, Spinner } from '@components/ui';
import { MeetingUploadForm, MeetingDraftCard } from '@components/features';
import {
  useMeetings,
  useMeeting,
  useMeetingAIStatus,
  useRetryMeeting,
  useDeleteMeeting,
  useApproveDrafts,
  usePermission,
} from '@hooks';
import { useUIStore } from '@store';

const STATUS = {
  PROCESSING: { label: 'Procesando', variant: 'warning' },
  READY: { label: 'Lista', variant: 'success' },
  FAILED: { label: 'Error', variant: 'danger' },
  STALE: { label: 'Interrumpida', variant: 'danger' },
};

const statusOf = (meeting) => (meeting.stale ? STATUS.STALE : STATUS[meeting.status]);

// Dates are stored at UTC midnight; read the calendar day straight from the ISO string.
const formatDay = (iso) => {
  if (!iso) return '';
  const [year, month, day] = iso.slice(0, 10).split('-');
  return `${day}/${month}/${year}`;
};

function MeetingListItem({ meeting, active, onSelect }) {
  const status = statusOf(meeting);
  const pending = meeting.draftCounts?.PENDING || 0;

  return (
    <button
      type="button"
      onClick={() => onSelect(meeting.id)}
      className={`w-full text-left rounded-xl px-3 py-2 transition-colors ${
        active ? 'bg-indigo-50 ring-1 ring-indigo-200' : 'hover:bg-slate-50'
      }`}
    >
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm font-medium text-slate-800 truncate">{meeting.title}</p>
        <Badge variant={status.variant} size="sm">{status.label}</Badge>
      </div>
      <p className="text-xs text-slate-500">
        {formatDay(meeting.meetingDate)} · {meeting.createdBy?.name}
        {meeting.status === 'READY' && ` · ${pending} pendiente${pending === 1 ? '' : 's'}`}
      </p>
    </button>
  );
}

MeetingListItem.propTypes = {
  meeting: PropTypes.object.isRequired,
  active: PropTypes.bool.isRequired,
  onSelect: PropTypes.func.isRequired,
};

function MeetingDetail({ meetingId, onDeleted }) {
  const { data, isLoading } = useMeeting(meetingId);
  const retry = useRetryMeeting();
  const deleteMeeting = useDeleteMeeting();
  const approveDrafts = useApproveDrafts();
  const { hasPermission } = usePermission();
  const setActiveView = useUIStore((state) => state.setActiveView);
  const setSelectedTaskId = useUIStore((state) => state.setSelectedTaskId);

  const [selected, setSelected] = useState(() => new Set());
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [error, setError] = useState('');

  const meeting = data?.data;
  const drafts = meeting?.drafts || [];
  const pending = drafts.filter((d) => d.status === 'PENDING');
  const others = drafts.filter((d) => d.status !== 'PENDING');
  const canAssign = hasPermission('tareas.asignar');
  const selectedPending = pending.filter((d) => selected.has(d.id));
  const selectionNeedsAssign = selectedPending.some((d) => d.assigneeId) && !canAssign;

  if (isLoading || !meeting) {
    return (
      <Card className="flex justify-center py-16">
        <Spinner size="lg" className="text-indigo-500" />
      </Card>
    );
  }

  const status = statusOf(meeting);

  const toggle = (id) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const run = async (action) => {
    setError('');
    try {
      await action();
    } catch (err) {
      setError(err.message);
    }
  };

  const approveSelected = () =>
    run(async () => {
      await approveDrafts.mutateAsync({ meetingId: meeting.id, draftIds: selectedPending.map((d) => d.id) });
      setSelected(new Set());
    });

  const openTask = (taskId) => {
    setSelectedTaskId(taskId);
    setActiveView('task-detail');
  };

  return (
    <div className="space-y-4">
      <Card>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <h2 className="text-lg font-bold truncate">{meeting.title}</h2>
              <Badge variant={status.variant}>{status.label}</Badge>
            </div>
            <p className="text-xs text-slate-500">
              Reunión del {formatDay(meeting.meetingDate)} · {meeting.fileName} · subida por {meeting.createdBy?.name}
            </p>
          </div>
          <div className="flex gap-2">
            {(meeting.status === 'FAILED' || meeting.stale) && (
              <Button size="sm" loading={retry.isPending} onClick={() => run(() => retry.mutateAsync(meeting.id))}>
                Reintentar
              </Button>
            )}
            <Button variant="ghost" size="sm" className="text-red-600" onClick={() => setConfirmDelete(true)}>
              Eliminar
            </Button>
          </div>
        </div>

        {meeting.status === 'PROCESSING' && !meeting.stale && (
          <div className="mt-4 flex items-center gap-3 text-sm text-slate-600">
            <Spinner size="sm" className="text-indigo-500" />
            Analizando la reunión con IA. Suele tardar entre 1 y 3 minutos; puedes salir de esta pantalla.
          </div>
        )}
        {meeting.stale && (
          <p className="mt-4 text-sm text-red-600">El proceso se interrumpió (reinicio del servidor). Reinténtalo.</p>
        )}
        {meeting.status === 'FAILED' && <p className="mt-4 text-sm text-red-600">{meeting.error}</p>}
        {meeting.summary && <p className="mt-4 text-sm text-slate-700 whitespace-pre-line">{meeting.summary}</p>}
        {error && <p className="mt-3 text-sm text-red-600">{error}</p>}
      </Card>

      {meeting.status === 'READY' && (
        <>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm text-slate-600">
              {pending.length} borrador{pending.length === 1 ? '' : 'es'} pendiente{pending.length === 1 ? '' : 's'}. Nada se crea hasta que lo apruebes; las tareas van al Backlog.
            </p>
            {pending.length > 0 && (
              <div className="flex items-center gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() =>
                    setSelected(selectedPending.length === pending.length ? new Set() : new Set(pending.map((d) => d.id)))
                  }
                >
                  {selectedPending.length === pending.length ? 'Quitar selección' : 'Seleccionar todos'}
                </Button>
                <Button
                  variant="success"
                  size="sm"
                  loading={approveDrafts.isPending}
                  disabled={selectedPending.length === 0 || selectionNeedsAssign}
                  onClick={approveSelected}
                >
                  Crear {selectedPending.length || ''} tarea{selectedPending.length === 1 ? '' : 's'}
                </Button>
              </div>
            )}
          </div>
          {selectionNeedsAssign && (
            <p className="text-xs text-amber-700">Hay borradores seleccionados con responsable y no tienes permiso para asignar.</p>
          )}

          {drafts.length === 0 && (
            <Card className="text-center text-sm text-slate-500 py-10">La IA no encontró acciones concretas en esta reunión.</Card>
          )}

          {[...pending, ...others].map((draft) => (
            <MeetingDraftCard
              // A new server version remounts the card, discarding the stale form.
              key={`${draft.id}-${draft.updatedAt}`}
              draft={draft}
              meetingId={meeting.id}
              members={meeting.assignableMembers}
              canAssign={canAssign}
              selected={selected.has(draft.id)}
              onToggleSelect={toggle}
              onOpenTask={openTask}
            />
          ))}
        </>
      )}

      <Modal
        isOpen={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        title="Eliminar reunión"
        footer={
          <>
            <Button variant="secondary" onClick={() => setConfirmDelete(false)}>
              Cancelar
            </Button>
            <Button
              variant="danger"
              loading={deleteMeeting.isPending}
              onClick={() =>
                run(async () => {
                  await deleteMeeting.mutateAsync(meeting.id);
                  setConfirmDelete(false);
                  onDeleted();
                })
              }
            >
              Eliminar
            </Button>
          </>
        }
      >
        <p className="text-slate-600">
          Se borran la transcripción y los borradores de <strong>{meeting.title}</strong>. Las tareas ya creadas se quedan.
        </p>
        {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
      </Modal>
    </div>
  );
}

MeetingDetail.propTypes = {
  meetingId: PropTypes.number.isRequired,
  onDeleted: PropTypes.func.isRequired,
};

export default function MeetingsView({ projectId }) {
  const [selectedId, setSelectedId] = useState(null);
  const { data: statusData } = useMeetingAIStatus();
  const { data, isLoading } = useMeetings(projectId);
  const meetings = useMemo(() => data?.data || [], [data]);
  const aiConfigured = statusData?.data?.configured !== false;

  // Falls back to the newest meeting while the chosen one is not in the list
  // (another project, deleted, or just uploaded and the list not refetched yet).
  const activeId = meetings.some((m) => m.id === selectedId) ? selectedId : (meetings[0]?.id ?? null);

  if (!projectId) {
    return <div className="p-8 text-center text-slate-500">Selecciona un proyecto para ver sus reuniones</div>;
  }

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-xl font-bold text-slate-800 dark:text-slate-100">Reuniones</h1>
        <p className="text-sm text-slate-500">
          Sube la transcripción de una reunión y la IA propone tareas, bugs y mejoras con su fecha compromiso. Todo queda en borrador hasta que lo apruebes.
        </p>
      </div>

      {!aiConfigured && (
        <Card className="border-l-4 border-l-red-500 text-sm text-red-700">
          El puente de IA no está configurado en el servidor (CLAUDE_BRIDGE_URL / CLAUDE_BRIDGE_KEY). No se pueden procesar reuniones.
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 items-start">
        <div className="space-y-4">
          <MeetingUploadForm projectId={projectId} disabled={!aiConfigured} onUploaded={(meeting) => setSelectedId(meeting.id)} />

          <Card padding="sm">
            <h3 className="px-2 pt-1 pb-2 text-sm font-semibold">Historial</h3>
            {isLoading && <Spinner size="sm" className="mx-auto my-4 text-indigo-500" />}
            {!isLoading && meetings.length === 0 && <p className="px-2 pb-2 text-xs text-slate-500">Todavía no hay reuniones.</p>}
            <div className="space-y-1">
              {meetings.map((meeting) => (
                <MeetingListItem key={meeting.id} meeting={meeting} active={meeting.id === activeId} onSelect={setSelectedId} />
              ))}
            </div>
          </Card>
        </div>

        <div className="lg:col-span-2">
          {activeId ? (
            // Keyed so selection and errors reset when switching meetings.
            <MeetingDetail key={activeId} meetingId={activeId} onDeleted={() => setSelectedId(null)} />
          ) : (
            <Card className="text-center text-sm text-slate-500 py-16">Sube una reunión para empezar.</Card>
          )}
        </div>
      </div>
    </div>
  );
}

MeetingsView.propTypes = {
  projectId: PropTypes.number,
};
